import BaseApi, { ServerListResponse, makeParamsSerializer } from '@lumapps/base-api';
import type { Video, VideoListQuery } from '@lumapps/play/api/types';
import { getLanguageHeader } from '@lumapps/play/utils';

import type { Playlist } from '../types';
import type {
    CreatePlaylistData,
    GetPlaylistParams,
    GetPlaylistVideosParams,
    GetPlaylistsResponse,
    UpdatePlaylistData,
} from './types';

/**
 * Playlist API.
 */
export const playlistApi = new BaseApi({
    version: BaseApi.versions.v2,
});

/**
 * Query keys object used to manage React Query keys
 *
 * @see https://tkdodo.eu/blog/effective-react-query-keys#use-query-key-factories
 */
export const playlistsQueryKeys = {
    playlists: (params?: VideoListQuery) => ['videos', 'playlists', params] as const,
    playlistVideos: (id?: string, params?: Omit<GetPlaylistVideosParams, 'fields'> & { fields?: (keyof Video)[] }) =>
        ['playlists', id, 'videos', params] as const,
};

/**
 * Fetches a list of playlists with full details.
 * Intended for use in edition interfaces.
 *
 * @param {VideoListQuery} [params] - Optional query parameters to filter the playlists.
 * @param {AbortSignal} [signal] - Optional signal to cancel the request.
 *
 * @example
 * getPlaylists({ maxResults: 5 }) // Returns a list of 5 playlists
 *
 * @returns {Promise<GetPlaylistsResponse>} Promise resolving to a list of playlists
 */
export async function getPlaylists(params?: VideoListQuery, signal?: AbortSignal) {
    const { data } = await playlistApi.get<GetPlaylistsResponse>(
        '/play/playlists',
        {
            params: { ...params, kinds: ['playlists'] },
            headers: { 'Accept-Language': getLanguageHeader() },
            signal,
        },
        true,
        true,
    );
    return data;
}

/** Function to cancel the get list of playlists */
export function cancelGetPlaylists(fetchKey?: string) {
    return playlistApi.cancel('', undefined, fetchKey);
}

export async function getPlaylist<Field extends keyof Playlist>(
    parameters: GetPlaylistParams<Field>,
    fetchKey?: string,
) {
    const { id, ...params } = parameters;
    const endpoint = params?.fields ? `/playlists/${id}` : `/play/playlists/${id}`;

    return playlistApi.get<Playlist>(
        endpoint,
        {
            headers: { 'Accept-Language': getLanguageHeader() },
            params: { ...params },
            paramsSerializer: makeParamsSerializer({ arrayFormat: 'comma', encode: false }),
        },
        true,
        true,
        fetchKey,
    );
}

/** Function to cancel the unique playlist get */
export function cancelGetPlaylist(fetchKey?: string) {
    return playlistApi.cancel('', undefined, fetchKey);
}

/**
 * Create a new playlist.
 *
 * @param {CreatePlaylistData} data - Payload needed to create a new playlist
 *
 * @example createPlaylist(data: CreatePlaylistData);
 *
 * Note: The id of the newly created playlist can be retrieved via the header "Content-Location" sent in the response.
 */
export async function createPlaylist(data: CreatePlaylistData): Promise<string> {
    const { headers } = await playlistApi.post('/play/playlists', data);
    if (!headers) {
        return '';
    }
    const contentLocationPlaylist = headers['content-location']?.split('/');
    const playlistId = contentLocationPlaylist[contentLocationPlaylist.length - 1];
    return playlistId;
}

/**
 * Update a playlist
 *
 * @param {string} playlistId - ID of the playlist we want to update.
 * @param {UpdatePlaylistData} data - Request body needed to update a playlist
 *
 * @example updatePlaylist('1', { status: 'published' });
 */
export async function updatePlaylist(playlistId: Playlist['id'], data: UpdatePlaylistData): Promise<void> {
    await playlistApi.patch(`/play/playlists/${playlistId}`, data);
}

/**
 * Delete a playlist
 *
 * @param {string} playlistId - ID of the playlist we want to delete.
 *
 * @example deletePlaylist('1');
 */
export async function deletePlaylist(playlistId: Playlist['id']): Promise<void> {
    await playlistApi.delete(`/play/playlists/${playlistId}`);
}

/**
 * Get the videos added to the given playlist
 *
 * @param {GetPlaylistVideosParams} parameters - The playlist id + list of query params
 *
 * @example getPlaylistVideos({
 *      id: '1234',
 *      maxResults: 4,
 *      fields: ['title', 'description']
 *     })
 */
export const getPlaylistVideos = async (
    parameters: GetPlaylistVideosParams,
    fetchKey?: string,
    signal?: AbortSignal,
) => {
    const { id, thumbnailSizes, ...params } = parameters;
    const endpoint = `/playlists/${id}/videos`;

    return playlistApi.get<ServerListResponse<Video>>(
        endpoint,
        {
            headers: { 'Accept-Language': getLanguageHeader() },
            params: {
                ...params,
                ...(thumbnailSizes && { ...thumbnailSizes }),
            },
            signal,
            paramsSerializer: makeParamsSerializer({ arrayFormat: 'comma', encode: false }),
        },
        true,
        true,
        fetchKey,
    );
};

/** Function to cancel the get of playlist videos */
export function cancelGetPlaylistVideos(parameters: GetPlaylistVideosParams, fetchKey?: string) {
    const { id, ...params } = parameters;
    const endpoint = `/playlists/${id}/videos`;

    return playlistApi.cancel(endpoint, params, fetchKey);
}
